import React, { useState, useRef, useCallback } from "react";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import CalculatorGenderSelector from "../../components/calculators/CalculatorGenderSelector";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import WeightInputGroup from "../../components/calculators/WeightInputGroup";
import { calculateLeanBodyWeight } from "../../utils/calculators/lbw";
import { rounding } from "../../utils/calculators/general";
import { isNumber } from "../../utils/isNumber";
import {
  validateHeightFromInputs,
  validateWeightFromInputs,
} from "../../utils/calculators/validation";
import { getHeightInCm } from "../../utils/lengthConverter";
import { getWeightInKg } from "../../utils/weightConverter";
import { useHeightInput, useWeightInput } from "../../hooks/calculatorHooks";
import useErrorFocus from "../../hooks/useErrorFocus";
import styles from "../../components/calculators/calculator.module.scss";

import formulaMale from "./images/etg_abg15_appendix_figure3_male-lean-body-weight.png";
import formulaFemale from "./images/etg_abg15_appendix_figure4_female-lean-body-weight.png";
import formulaBMI from "./images/etg_abg15_appendix_figure5_body-mass-index.png";

const LbwCalcApp = ({ isWebLayout = false }) => {
  const [sex, setSex] = useState("");
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const resultsRef = useRef(null);

  // Define field order for error focus management
  const fieldOrder = ["sex", "height", "weight"];

  // Use the global error focus hook
  useErrorFocus(errors, fieldOrder);

  const {
    height,
    heightUnit,
    feet,
    inches,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleUnitChange,
    clearHeightFields,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  const {
    weightValue: actualBodyWeight,
    weightUnit,
    handleWeightChange,
    handleWeightUnitChange,
    clearWeightFields,
    unitOptions: weightUnitOptions,
  } = useWeightInput();

  const wrappedHeightChange = useCallback(
    (e) => handleHeightChange(e, setErrors),
    [handleHeightChange],
  );
  const wrappedFeetChange = useCallback(
    (e) => handleFeetChange(e, setErrors),
    [handleFeetChange],
  );
  const wrappedInchesChange = useCallback(
    (e) => handleInchesChange(e, setErrors),
    [handleInchesChange],
  );
  const wrappedUnitChange = useCallback(
    (e) => handleUnitChange(e, setErrors),
    [handleUnitChange],
  );
  const wrappedWeightChange = useCallback(
    (e) => handleWeightChange(e, setErrors),
    [handleWeightChange],
  );
  const wrappedWeightUnitChange = useCallback(
    (e) => handleWeightUnitChange(e, setErrors),
    [handleWeightUnitChange],
  );

  function validateFields() {
    let fieldErrors = {};

    const heightError = validateHeightFromInputs(
      heightUnit,
      height,
      feet,
      inches,
      true, // Use normal constants instead of relaxed
    );
    if (heightError) {
      fieldErrors.height = heightError;
    }

    const weightError = validateWeightFromInputs(
      actualBodyWeight,
      weightUnit,
      true, // Already using normal constants
    );
    if (weightError) {
      fieldErrors.weight = weightError;
    }

    if (!sex) fieldErrors.sex = "Please select a sex";
    return fieldErrors;
  }

  const handleClear = () => {
    setSex("");
    clearHeightFields();
    clearWeightFields();
    setResult("");
    setErrors({});
    setShowResult(false);
  };

  function handleCalculate(e) {
    e.preventDefault();
    const fieldErrors = validateFields();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setShowResult(false);
      setResult("");
      return;
    }

    const heightInCm = getHeightInCm(heightUnit, height, feet, inches);
    const weightInKg = getWeightInKg(actualBodyWeight, weightUnit);
    const value = calculateLeanBodyWeight({
      sex,
      heightCm: heightInCm,
      actualBodyWeightKg: weightInKg,
    });

    if (isNumber(value)) {
      setResult(rounding(value, 0).toString());
      setShowResult(true);
      setErrors({});
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    } else {
      setResult("");
      setShowResult(false);
    }
  }

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      <p>This calculator is based on the formula:</p>
    </div>
  );

  const formulaSection = (
    <div className={isWebLayout ? "" : styles.formulaBlock}>
      <img
        src={formulaMale}
        alt="Lean Body Weight Male Formula"
        className={isWebLayout ? "" : styles.formulaImage}
      />
      <img
        src={formulaFemale}
        alt="Lean Body Weight Female Formula"
        className={isWebLayout ? "" : styles.formulaImage}
      />
      <img
        src={formulaBMI}
        alt="Body Mass Index Formula"
        className={styles.formulaImageBMI}
      />
    </div>
  );

  const calculatorTitle = "Lean body weight calculator";

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <CalculatorGenderSelector
        value={sex}
        onChange={(value) => {
          setSex(value);
          setErrors((prev) => ({ ...prev, sex: "" }));
        }}
        error={errors.sex}
      />
      <HeightInputGroup
        idPrefix="lbw"
        height={height}
        heightUnit={heightUnit}
        feet={feet}
        inches={inches}
        onHeightChange={wrappedHeightChange}
        onFeetChange={wrappedFeetChange}
        onInchesChange={wrappedInchesChange}
        onUnitChange={wrappedUnitChange}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
        data-testid="height-input"
      />
      <WeightInputGroup
        idPrefix="lbw"
        label="Actual body weight"
        weightValue={actualBodyWeight}
        weightUnit={weightUnit}
        onWeightChange={wrappedWeightChange}
        onWeightUnitChange={wrappedWeightUnitChange}
        error={errors.weight}
        unitOptions={weightUnitOptions}
        isWebLayout={isWebLayout}
        data-testid="weight-input"
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const resultsInner = (
    <CalculatorResults
      label="Lean body weight"
      value={result}
      unit={result ? <span className={styles.resultUnit}>kg</span> : undefined}
      showResult={showResult}
    />
  );

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      formulaSection={formulaSection}
      panel={panel}
      results={<div ref={resultsRef}>{resultsInner}</div>}
      isWebLayout={isWebLayout}
    />
  );
};

export default LbwCalcApp;
